import React, { useRef } from 'react';
import type { PageProps } from 'keycloakify/login/pages/PageProps';
import { Flexbox } from '@bankiru/ui-kit/components/Flexbox';
import { Indent } from '@bankiru/ui-kit/components/Indent';
import Spin from '@bankiru/ui-kit/core/Spin';
import Text from '@bankiru/ui-kit/core/Text';

import { formatCountdown } from '../../../helpers/formatCountdown';
import { useCountdown } from '../../../hooks/useCountdown';
import { useInterval } from '../../../hooks/useInterval';
import type { I18n } from '../../i18n';
import type { KcContext } from '../../KcContext';
import { MobileIdStatus } from './types';

type MobileIdFormProps = PageProps<Extract<KcContext, { pageId: 'mobile-push.ftl' }>, I18n>;

const WAITING_TIME = 50;
const CHECK_INTERVAL = 3_000;

export default function LoginMobileId(props: MobileIdFormProps) {
	const formRef = useRef<HTMLFormElement | null>(null);
	const isSubmitted = useRef(false);

	const { kcContext, i18n, doUseDefaultCss, Template, classes } = props;

	const { url, mobileStatus, correlationId } = kcContext;

	const { msg, msgStr } = i18n;

	const timeLeft = useCountdown(WAITING_TIME);

	const handleFail = () => {
		if (!isSubmitted.current) {
			isSubmitted.current = true;
			formRef.current?.submit();
		}
	};

	const checkStatus = () => {
		try {
			if (timeLeft <= 0) {
				handleFail();
			}

			fetch(mobileStatus, {
				method: 'POST',
				body: JSON.stringify({ correlationId }),
				headers: {
					'Content-Type': 'application/json',
				},
			})
				.then(result => result.json())
				.then((response: { status: MobileIdStatus }) => {
					if (response.status === MobileIdStatus.WAITING) {
						return;
					}

					handleFail();
				})
				.catch(handleFail);
		} catch {
			if (!isSubmitted.current) {
				isSubmitted.current = true;
				formRef.current?.submit();
			}
		}
	};

	useInterval(checkStatus, CHECK_INTERVAL);

	return (
		<Template
			kcContext={kcContext}
			i18n={i18n}
			doUseDefaultCss={doUseDefaultCss}
			classes={{
				...classes,
				kcFormCardClass: 'gray',
				kcBodyClass: 'gray',
			}}
			headerNode={<div />}
			displayInfo={false}
		>
			<div id="kc-form">
				<div id="kc-form-wrapper" className="kcFormWrapper">
					<form
						id="kc-form-login"
						action={`${url.loginAction}`}
						method="post"
						ref={formRef}
					>
						<Indent margin="32 0 0 0">
							<Flexbox flexDirection="column" alignItems="center">
								<Text align="center" color="primary" size="fs18" weight="medium">
									{msgStr('mobileIdTitle')}
								</Text>
								<Text color="secondary" size="fs12">
									{msg('mobileIdDescription')}
								</Text>
								<Flexbox flexDirection="column" alignItems="center">
									<Indent margin="80 0">
										<Spin size="medium" />
									</Indent>
								</Flexbox>
								<Text align="center" color="secondary" size="fs12">
									{`${msgStr('timeLeft')}:`}
								</Text>
								<Text align="center" color="primary" size="fs18" weight="medium">
									{formatCountdown(timeLeft)}
								</Text>
							</Flexbox>
						</Indent>
					</form>
				</div>
			</div>
		</Template>
	);
}
